import { useState, useEffect, useMemo, useRef } from 'react'
import { Typography, Box } from '@mui/material'
import {
  StockProjectionExceptionGraphType,
  StockProjectionExceptionTableData,
  StockProjectionExceptionURLParams,
  WeekRangeConfig,
} from './StockProjectionException.types'
import { useParams } from 'react-router-dom'
import {
  useStockProjectExceptionsData,
  useStockProjectExceptionsGraphData,
  useStockProjectionWaterfallAvailability,
  useStockProjectionWaterfallData,
} from './StockProjectionException.data'
import { Loader } from 'Common/Loader/Loader'
import _ from 'lodash'
import { EditWeekRange } from './EditWeekRange.view'
import { SpeGraph } from './SPE.graph'
import { SPETable } from './SPE.table'
import { Legend } from './Legend/Legend.view'
import { LegendType } from './Legend/Legend.type'
import { getISOWeek, parse } from 'date-fns'
import {
  StockWaterfallGraphs,
  useStockWaterfallWalk,
} from 'Common/Charts/StockWaterfall/StockWaterfall.view'
import { StockWaterfallLegend } from 'Common/Charts/StockWaterfall/StockWaterfallLegend.view'
import { StockWaterfallToggle } from 'Common/Charts/StockWaterfall/StockWaterfallToggle.view'
import {
  DEFAULT_STOCK_WATERFALL_VIEW,
  StockWaterfallView,
} from 'Common/Charts/StockWaterfall/StockWaterfall.constants'
import {
  isLedgerNotAvailable,
  usePlanBuiltByV2Engine,
} from 'Common/Charts/StockWaterfall/stockWaterfall.data'

const DEFAULT_WEEK_RANGE = 10

const legends: LegendType[] = [
  {
    name: 'FD',
    color: '#F59E0B',
    abbreviation: 'Forecast Demand Signal',
    shape: 'square',
  },
  {
    name: 'SITA',
    color: '#EF4444',
    abbreviation: 'Stock In Transit Arrival',
    shape: 'square',
  },
  {
    name: 'PVA',
    color: '#1D4ED8',
    abbreviation: 'Production Volume Arrival',
    shape: 'square',
  },
  {
    name: 'SP',
    color: '#198754',
    abbreviation: 'Stock Policy',
    shape: 'line',
  },
]

export const StockProjectionExceptionPage = () => {
  // const [stockProjectionExceptionData, setStockProjectionExceptionData] =
  //   useState<StockProjectionExceptionTableData[] | null>(null)

  // const [selectedSku, setSelectedSku] = useState<string | null>(null)
  const [selectedSkuName, setSelectedSkuName] = useState<string | null>(null)
  const [selectedSkus, setSelectedSkus] = useState<string[]>([])
  const [weekRangeConfig, setWeekRangeConfig] = useState<WeekRangeConfig>({
    start: null,
    end: null,
  })
  const [selectedGraphs, setSelectedGraphs] = useState<string[]>([])
  const [hoveredGraphSite, setHoveredGraphSite] = useState<string | null>(null)

  const weeksRef = useRef<
    | null
    | {
        key: string
        value: number
        index: number
      }[]
  >(null)

  const { planId, sourcesiteId, workcentreId } =
    useParams<StockProjectionExceptionURLParams>()

  const {
    data: stockProjectionExceptionTableData,
    isLoading: isStockProjectionExceptionTableDataLoading,
    isFetching: isStockProjectionExceptionTableDataFetching,
  } = useStockProjectExceptionsData({
    planId,
    sourcesiteId,
    workcentreId,
    weekRangeConfig,
  })

  const {
    data: stockProjectionExceptionGraphData,
    isLoading: isStockProjectionExceptionGraphDataLoading,
  } = useStockProjectExceptionsGraphData({
    planId,
    sourcesiteId,
    workcentreId,
    skuCodes: selectedSkus,
  })

  // Stock waterfall v2: a New / Classic switch on plans with a ledger.
  const [waterfallView, setWaterfallView] = useState<StockWaterfallView>(
    DEFAULT_STOCK_WATERFALL_VIEW
  )
  const { builtByV2Engine, planStartWeek } = usePlanBuiltByV2Engine(planId)
  const { isSuccess: hasLedger } = useStockProjectionWaterfallAvailability({
    planId,
    sourcesiteId,
    workcentreId,
    skuCodes: [],
    enabled: builtByV2Engine,
  })
  const {
    data: waterfallRows,
    error: waterfallError,
    isLoading: isWaterfallLoading,
  } = useStockProjectionWaterfallData({
    planId,
    sourcesiteId,
    workcentreId,
    skuCodes: selectedSkus,
    enabled: hasLedger && waterfallView === 'new',
  })
  const showWaterfall =
    hasLedger &&
    waterfallView === 'new' &&
    !isLedgerNotAvailable(waterfallError)
  const waterfallSites = useStockWaterfallWalk(
    showWaterfall ? waterfallRows : undefined,
    {
      sites: selectedGraphs,
      startDateKey: weekRangeConfig.start,
      endDateKey: weekRangeConfig.end,
    }
  )

  const weekDropDown = useMemo(() => {
    if (stockProjectionExceptionTableData && weeksRef.current === null) {
      weeksRef.current = stockProjectionExceptionTableData.weeks
    }
    return weeksRef.current ?? []
  }, [stockProjectionExceptionTableData])

  useEffect(() => {
    if (
      weekDropDown?.length &&
      weekRangeConfig.start === null &&
      weekRangeConfig.end === null
    ) {
      const { constrainingHorizon } =
        stockProjectionExceptionTableData.planDetails
      const initialEndVal =
        constrainingHorizon > 0 ? constrainingHorizon : DEFAULT_WEEK_RANGE
      const endVal =
        weekDropDown.length > initialEndVal
          ? weekDropDown[initialEndVal - 1].value
          : weekDropDown[weekDropDown.length - 1].value

      setWeekRangeConfig({
        start: weekDropDown[0].value,
        end: endVal,
      })
    }
  }, [weekDropDown])

  useEffect(() => {
    // setStockProjectionExceptionData(stockProjectionExceptionTableData?.data)
    if (stockProjectionExceptionTableData?.data.length > 0) {
      if (!selectedSkuName) {
        const skuName = stockProjectionExceptionTableData?.data[0].skuName
        //setSelectedSku(sku)
        setSelectedSkuName(skuName)
      }

      if (selectedGraphs.length === 0) {
        const siteCodes = new Set<string>()
        stockProjectionExceptionTableData.data.forEach(
          (dataRow: StockProjectionExceptionTableData) => {
            const { siteCode } = dataRow
            siteCodes.add(siteCode)
          }
        )
        setSelectedGraphs([...siteCodes])
      }
    }
  }, [stockProjectionExceptionTableData])

  const handleTableRowClick = (data: StockProjectionExceptionTableData[]) => {
    const skuName = data.length === 1 ? data[0]?.skuName : ''
    setSelectedSkuName(skuName)
    setSelectedSkus(data.map((d) => d.skuCode))
  }

  const handleSelectedGraphsChange = (sites: string[]) => {
    setSelectedGraphs(sites)
  }

  const formattedGraphData = useMemo(() => {
    if (!stockProjectionExceptionGraphData) {
      return undefined
    }

    let rows = []
    if (!Array.isArray(stockProjectionExceptionGraphData)) {
      // Casting to an array incase data is an object.
      rows = [{ ...stockProjectionExceptionGraphData }]
    } else {
      rows = [
        ...(stockProjectionExceptionGraphData as StockProjectionExceptionTableData[]),
      ]
    }

    const selectedGraphsSet = new Set(selectedGraphs)
    rows = rows.filter((row) => selectedGraphsSet.has(row.replenishmentSite))

    const startWeek = weekRangeConfig.start ?? 0
    const endWeek = weekRangeConfig.end ?? 50000000
    // Group by replenishment site.
    const formatedDataBySite = _.groupBy(rows, (row) => row.replenishmentSite)

    const weeksCopy = new Set(
      rows
        .filter(
          (row: StockProjectionExceptionTableData) =>
            row.dateKey >= startWeek && row.dateKey <= endWeek
        )
        .map(
          (row: StockProjectionExceptionTableData) =>
            row.stockProjectionExceptionWeek
        )
    )
    const weeks = []
    for (const week of weeksCopy) {
      weeks.push(week, week, week)
    }

    const dateKeys = []
    const dateKeyCopy = new Set(
      rows
        .filter(
          (row: StockProjectionExceptionTableData) =>
            row.dateKey >= startWeek && row.dateKey <= endWeek
        )
        .map((row: StockProjectionExceptionTableData) => {
          const date = parse(row.dateKey.toString(), 'yyyyMMdd', new Date())
          const isoWeekNumber = getISOWeek(date)
          // Get the year. year is added because isoweek is repeating after 52 weeks.
          const year = date.getFullYear()
          return `${isoWeekNumber}_${year}`
        })
    )

    for (const dateKey of dateKeyCopy) {
      dateKeys.push(dateKey, dateKey, dateKey)
    }

    const plotData = []
    let y_max = Infinity * -1
    let y_min = Infinity
    const sites = []
    for (const replenishmentSite in formatedDataBySite) {
      sites.push(replenishmentSite)
      const weeklyDetails = formatedDataBySite[replenishmentSite]
      const detailLength = weeklyDetails.length
      const { weekStartInventory: base } = weeklyDetails[0]
      const name = `${replenishmentSite}`
      const safetystockVolumes: number[] = []
      const exceptions: number[] = []
      const toolTipTexts: string[] = []
      const FD: number[] = []
      const SITA: number[] = []
      const PVA: number[] = []
      for (let i = 0; i < detailLength; i++) {
        const {
          stockinTransitArrival,
          safetyStockVolume,
          replenishmentPlanArrival,
          forecastDemandSignal,
          stockProjectionExceptionWeek,
          weekStartInventory,
          minimumInventory,
          dateKey,
          productionDateFormatted,
        } = weeklyDetails[i]

        if (dateKey >= startWeek && dateKey <= endWeek) {
          const text =
            `<b>Week:  </b>${stockProjectionExceptionWeek}<br>` +
            ` <b>Start of the week inventory: </b>${(+weekStartInventory).toFixed(
              2
            )}<br>` +
            ` <b>Forecast demand volume: </b>${(+forecastDemandSignal).toFixed(
              2
            )}<br>` +
            ` <b>Stock in transit arrival: </b>${(+stockinTransitArrival).toFixed(
              2
            )}<br>` +
            ` <b>Production volume arrival: </b>${(+replenishmentPlanArrival).toFixed(
              2
            )}<br>` +
            ` <b>Stock policy: </b>${(+safetyStockVolume).toFixed(2)}<br>` +
            ` <b>Minimum week inventory: </b>${(+minimumInventory).toFixed(
              2
            )}<br>` +
            ` <b>Week of production: </b>${productionDateFormatted}<br>`

          toolTipTexts.push(text, text, text)
          FD.push(Number(forecastDemandSignal.toFixed(2)), 0, 0)
          SITA.push(0, stockinTransitArrival, 0)
          PVA.push(0, 0, replenishmentPlanArrival)
          exceptions.push(
            Number((forecastDemandSignal * -1).toFixed(2)),
            Number(stockinTransitArrival.toFixed(2)),
            Number(replenishmentPlanArrival.toFixed(2))
          )
          safetystockVolumes.push(
            safetyStockVolume,
            safetyStockVolume,
            safetyStockVolume
          )
        }
      }
      const FD_MASK: number[] = []
      const SITA_MASK: number[] = []
      const PVA_MASK: number[] = []
      exceptions.reduce(
        (previousValue: number, currentValue: number, currentIndex: number) => {
          const value = Number((previousValue + currentValue).toFixed(2))
          y_max = Math.max(value, y_max, safetystockVolumes[currentIndex])
          y_min = Math.min(y_min, value, safetystockVolumes[currentIndex])
          FD_MASK.push(FD[currentIndex])
          FD_MASK[currentIndex] = FD[currentIndex] !== 0 ? value : 0
          SITA_MASK.push(SITA[currentIndex])
          SITA_MASK[currentIndex] = SITA[currentIndex] !== 0 ? previousValue : 0
          PVA_MASK.push(PVA[currentIndex])
          PVA_MASK[currentIndex] = PVA[currentIndex] !== 0 ? previousValue : 0
          return value
        },
        base
      )

      plotData.push({
        base,
        name,
        weeks,
        exceptions,
        safetystockVolumes,
        toolTipTexts,
        FD,
        SITA,
        PVA,
        FD_MASK,
        SITA_MASK,
        PVA_MASK,
        ISOWeeks: dateKeys,
      })
    }
    return { plotData, y_max, y_min, sites }
  }, [stockProjectionExceptionGraphData, weekRangeConfig, selectedGraphs])

  const plotGraphs = (
    formattedGraphData: StockProjectionExceptionGraphType[],
    y_max: number,
    y_min: number
  ) => {
    return formattedGraphData
      .filter((mapData: StockProjectionExceptionGraphType) =>
        selectedGraphs.length > 0 ? selectedGraphs.includes(mapData.name) : true
      )
      .map(
        (
          mapData: StockProjectionExceptionGraphType,
          index: number,
          baseArray: StockProjectionExceptionGraphType[]
        ) => {
          return (
            <Box
              key={`spe-graph-${mapData.name}-${index}`}
              data-site-code={mapData.name}
              height={baseArray.length === 1 ? '302px' : '151px'}
              mb={baseArray.length - 1 === index ? 0 : 0.25}
              onMouseEnter={() => setHoveredGraphSite(mapData.name)}
              onMouseLeave={() => setHoveredGraphSite(null)}
            >
              <SpeGraph
                graphData={mapData}
                yAxisRange={[y_min, y_max]}
                oneGraph={baseArray.length === 1}
              />
            </Box>
          )
        }
      )
  }

  const handleWeekRangeChange = (config: WeekRangeConfig) => {
    setWeekRangeConfig(config)
  }

  return (
    <Box display="flex" flexDirection="column" flex="auto">
      <Box>
        {selectedSkus?.length > 0 ? (
          <Box
            sx={{
              border: '1px solid rgba(224, 224, 224, 1)',
              borderRadius: '4px',
            }}
          >
            <Box>
              <Box
                sx={{
                  width: '100%',
                  background: (theme) => theme.palette.common.white,
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '10px 20px',
                }}
              >
                <Box display="flex" flexDirection="column">
                  <Box>
                    <Typography variant="h4">
                      {selectedSkuName && `${selectedSkuName}`}
                    </Typography>
                  </Box>
                  <Box>
                    {showWaterfall ? (
                      <StockWaterfallLegend sites={waterfallSites} />
                    ) : (
                      <Legend legends={legends} />
                    )}
                  </Box>
                </Box>
                <Box aria-label="week range and sites container" display="flex">
                  {hasLedger && (
                    <StockWaterfallToggle
                      value={waterfallView}
                      onChange={setWaterfallView}
                    />
                  )}
                  <Box>
                    <EditWeekRange
                      weeks={weekDropDown}
                      config={weekRangeConfig}
                      handleWeekRangeChange={handleWeekRangeChange}
                      loading={
                        isStockProjectionExceptionGraphDataLoading ||
                        isStockProjectionExceptionTableDataLoading
                      }
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              marginTop={0.5}
              marginBottom={0}
              sx={{
                minHeight: '310px',
                maxHeight: '310px',
                overflowX: 'hidden',
                overflowY: 'auto',
              }}
            >
              {showWaterfall ? (
                isWaterfallLoading ? (
                  <Loader />
                ) : (
                  <StockWaterfallGraphs
                    sites={waterfallSites}
                    planStartWeek={planStartWeek}
                    onSiteHover={setHoveredGraphSite}
                  />
                )
              ) : isStockProjectionExceptionGraphDataLoading ? (
                <Loader />
              ) : (
                formattedGraphData &&
                selectedGraphs &&
                plotGraphs(
                  formattedGraphData.plotData,
                  formattedGraphData.y_max,
                  formattedGraphData.y_min
                )
              )}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              height: '360px',
              display: 'flex',
              justifyContent: 'center',
              fontWeight: 'bold',
            }}
          >
            <Typography variant="h3">
              Please select any SKU&apos;s in the below table to view stock
              projection.
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ backgroundColor: '#FFF', marginTop: '5px' }}>
        <SPETable
          data={stockProjectionExceptionTableData?.data ?? []}
          handleRowClick={handleTableRowClick}
          isLoading={
            isStockProjectionExceptionTableDataLoading ||
            isStockProjectionExceptionTableDataFetching
          }
          handleSelectedGraphsChange={handleSelectedGraphsChange}
          selectedSkus={selectedSkus}
          selectedSkuName={selectedSkuName}
          hoveredGraphSite={hoveredGraphSite}
        />
      </Box>
    </Box>
  )
}
